import { FC, useMemo } from 'react'
import { Report } from 'common/Report/Report'
import { ChartData, ChartOptions, ChartType } from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { WithReportLoader } from 'common/WithLoader/WithLoader'
import { useGetTransitionQuery } from 'services/api/transitionApi'
import { useTransitionId } from 'context/TransitionContext'
import { useGetWeeklyReportQuery, useUpdateQueryWeeklyReportNotesMutation } from 'services/api/queryApi'
import { WeeklyQueryStatusReportValue } from 'types'

const getConfig = (title: string): ChartOptions => ({
  maintainAspectRatio: false,
  responsive: true,
  interaction: {
    mode: 'index'
  },
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
      fullSize: false,
      align: 'center',
      labels: {
        boxWidth: 40,
        font: {
          size: 12
        }
      }
    },
    title: {
      display: true,
      text: title,
      align: 'start',
      padding: {
        bottom: 20
      }
    },
    tooltip: {
      intersect: false
    }
  },
  scales: {
    x: {
      stacked: true
    },
    y: {
      stacked: true
    }
  }
})

const initialData = (): ChartData<ChartType, number[]> => ({
  labels: [],
  datasets: [
    {
      label: 'closed',
      backgroundColor: '#02c12b',
      maxBarThickness: 30,
      data: []
    },
    {
      label: 'sideletter',
      backgroundColor: '#009ee5',
      maxBarThickness: 30,
      data: []
    },
    {
      label: 'workpack',
      backgroundColor: '#9929bd',
      maxBarThickness: 30,
      data: []
    },
    {
      label: 'delayed',
      backgroundColor: '#e20000',
      maxBarThickness: 30,
      data: []
    },
    {
      label: 'pending',
      backgroundColor: '#ff9e20',
      maxBarThickness: 30,
      data: []
    },
    {
      label: 'open',
      backgroundColor: '#ffef00',
      maxBarThickness: 30,
      data: []
    },
    {
      label: 'new',
      backgroundColor: '#a7a7a7',
      maxBarThickness: 30,
      data: []
    }
  ]
})
const chartData = (data: WeeklyQueryStatusReportValue[]): ChartData => {
  return data.reduce((cd, { week, ...counts }, i) => {
    cd.labels?.push(`W${week}`)
    cd.datasets[0].data.push((cd.datasets[0].data[i - 1] ?? 0) + counts.closedQueriesCount)
    cd.datasets[1].data.push((cd.datasets[1].data[i - 1] ?? 0) + counts.sideletterQueriesCount)
    cd.datasets[2].data.push((cd.datasets[2].data[i - 1] ?? 0) + counts.workpackQueriesCount)
    cd.datasets[3].data.push((cd.datasets[3].data[i - 1] ?? 0) + counts.delayedQueriesCount)
    cd.datasets[4].data.push((cd.datasets[4].data[i - 1] ?? 0) + counts.pendingQueriesCount)
    cd.datasets[5].data.push((cd.datasets[5].data[i - 1] ?? 0) + counts.openQueriesCount)
    cd.datasets[6].data.push((cd.datasets[6].data[i - 1] ?? 0) + counts.newQueriesCount)
    return cd
  }, initialData())
}

export const WeeklyQueryStatusReport: FC = () => {
  const { data: transitionData } = useGetTransitionQuery(useTransitionId())
  const transition = transitionData!
  const { transitionId } = transition
  const { data: weeklyReport, isLoading } = useGetWeeklyReportQuery(transitionId)
  const [updateNotes, { originalArgs }] = useUpdateQueryWeeklyReportNotesMutation()
  const config = useMemo(() => getConfig(transition.name), [transition.name])
  const data = weeklyReport?.data ?? []
  const notes = originalArgs?.notes ?? weeklyReport?.notes ?? ''

  const ChartElement = useMemo(() => {
    return <Bar type={'bar'} data={chartData(data)} options={config} />
  }, [data, config])

  return (
    <Report
      title={'Query Status'}
      info={'Number of closed and queries managed by sideletters in relation to all queries.'}
      reportNote={{
        note: notes,
        saveNote: (notes) => updateNotes({ notes, transitionId })
      }}>
      <WithReportLoader
        noWrapper
        loading={isLoading}
        hasResults={true}>
        {ChartElement}
      </WithReportLoader>
    </Report>
  )
}
